import { useRef, useState } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { saveAs } from 'file-saver';
import {
  UserEntity,
  QrMatchRequestDto,
  QrMatchRequestStatus,
  useFetchUserQrCodeQuery,
  useFetchUserQrRequestsQuery,
  useRespondUserQrRequestMutation,
} from 'src/store/userApi';
import { Card } from 'src/ui/Card';
import { Button } from 'src/ui/Button';
import { Badge } from 'src/ui/Badge';
import { Avatar } from 'src/ui/Avatar';
import { Spinner } from 'src/ui/Spinner';
import { EmptyState } from 'src/ui/EmptyState';
import {
  QrPairPickerModal,
  QrPairResetModal,
  QrPairTarget,
} from './qrPairReset';

const slugify = (s: string) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const STATUS_COLOR: Record<QrMatchRequestStatus, string> = {
  pending: 'amber',
  accepted: 'green',
  declined: 'red',
  cancelled: 'gray',
  expired: 'gray',
};

const STATUS_LABEL: Record<QrMatchRequestStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

interface UserQrCodeProps {
  selectedUser: UserEntity;
}

const UserQrCode = ({ selectedUser }: UserQrCodeProps) => {
  const userId = selectedUser.id;

  const {
    data: qr,
    isLoading: qrLoading,
    isError: qrError,
  } = useFetchUserQrCodeQuery(userId);
  const { data: requests } = useFetchUserQrRequestsQuery(userId);
  const [respond, respondStatus] = useRespondUserQrRequestMutation();

  const [copied, setCopied] = useState(false);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<QrPairTarget | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const onCopyLink = () => {
    if (!qr) return;
    navigator.clipboard.writeText(qr.payload);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const onDownloadPng = () => {
    if (!qr) return;
    const name = selectedUser.profile?.name || selectedUser.email;
    canvasRef.current?.toBlob((blob) => {
      if (blob) saveAs(blob, `${slugify(name)}-qr.png`);
    }, 'image/png');
  };

  const onRespond = async (requestId: string, action: 'accept' | 'decline') => {
    setRespondingId(requestId);
    try {
      await respond({ userId, requestId, action }).unwrap();
    } finally {
      setRespondingId(null);
    }
  };

  const incoming = requests ?? [];

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 340px) 1fr',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card pad>
          {qrLoading ? (
            <div
              className="row"
              style={{ justifyContent: 'center', padding: 40 }}
            >
              <Spinner />
            </div>
          ) : qrError || !qr ? (
            <EmptyState
              icon="alert-triangle"
              title="Couldn't load QR code"
              desc="Try reopening this tab."
            />
          ) : (
            <div
              className="col gap-3"
              style={{ alignItems: 'center', textAlign: 'center' }}
            >
              <QRCodeSVG
                value={qr.payload}
                size={240}
                level="M"
                includeMargin
              />
              <QRCodeCanvas
                ref={canvasRef}
                value={qr.payload}
                size={1024}
                level="M"
                includeMargin
                style={{ position: 'absolute', left: -9999, top: -9999 }}
              />
              <span
                className="mono text-secondary truncate"
                style={{ fontSize: 'var(--fs-xs)', maxWidth: '100%' }}
              >
                {qr.payload}
              </span>
              <div
                className="row gap-2"
                style={{ flexWrap: 'wrap', justifyContent: 'center' }}
              >
                <Button size="sm" leftIcon="copy" onClick={onCopyLink}>
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
                <Button size="sm" leftIcon="download" onClick={onDownloadPng}>
                  Download PNG
                </Button>
              </div>
            </div>
          )}
        </Card>

        <Card pad>
          <div className="col gap-3">
            <div className="row gap-2" style={{ alignItems: 'center' }}>
              <div
                className="grow"
                style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}
              >
                Incoming requests
              </div>
              <Button
                size="sm"
                variant="ghost"
                leftIcon="refresh"
                onClick={() => setPickerOpen(true)}
              >
                Reset with user…
              </Button>
            </div>
            {incoming.length === 0 ? (
              <EmptyState
                icon="qr-code"
                title="No requests yet"
                desc="Scan this code with another account to send a match request."
              />
            ) : (
              <div className="col gap-2">
                {incoming.map((r) => (
                  <QrRequestRow
                    key={r.id}
                    request={r}
                    responding={
                      respondingId === r.id && respondStatus.isLoading
                    }
                    onRespond={(action) => onRespond(r.id, action)}
                    onReset={() =>
                      setResetTarget({
                        userId: r.requester.userId,
                        name: r.requester.name,
                        otherUserId: r.owner.userId,
                        otherName: r.owner.name,
                      })
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      <QrPairPickerModal
        open={pickerOpen}
        selectedUser={selectedUser}
        onClose={() => setPickerOpen(false)}
        onPick={setResetTarget}
      />
      <QrPairResetModal
        target={resetTarget}
        onClose={() => setResetTarget(null)}
      />
    </div>
  );
};

interface QrRequestRowProps {
  request: QrMatchRequestDto;
  responding: boolean;
  onRespond: (action: 'accept' | 'decline') => void;
  onReset: () => void;
}

const QrRequestRow = ({
  request,
  responding,
  onRespond,
  onReset,
}: QrRequestRowProps) => (
  <div
    className="row gap-2"
    style={{
      alignItems: 'center',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      padding: '8px 10px',
    }}
  >
    <Avatar
      name={request.requester.name}
      src={request.requester.photoUrl}
      size={32}
    />
    <div className="grow" style={{ minWidth: 0 }}>
      <div
        className="truncate"
        style={{ fontSize: 'var(--fs-sm)', fontWeight: 500 }}
      >
        {request.requester.name}
      </div>
      <div className="text-muted" style={{ fontSize: 'var(--fs-2xs)' }}>
        {fmtDateTime(request.createdAt)}
      </div>
    </div>
    <Badge color={STATUS_COLOR[request.status]} size="sm">
      {STATUS_LABEL[request.status]}
    </Badge>
    <div className="row gap-1">
      {request.status === 'pending' && (
        <>
          <Button
            size="sm"
            leftIcon="check"
            loading={responding}
            onClick={() => onRespond('accept')}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leftIcon="x"
            disabled={responding}
            onClick={() => onRespond('decline')}
          >
            Decline
          </Button>
        </>
      )}
      <Button
        size="sm"
        variant="danger-ghost"
        leftIcon="trash"
        disabled={responding}
        onClick={onReset}
      >
        Reset
      </Button>
    </div>
  </div>
);

export default UserQrCode;
